import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { warning } from "../pure/notifications"
import { numberWithCommas } from "../pure/utils"

const FOCUSABLE_QUERY = 'input:not([type=hidden]):not([disabled]):not(.skip-tab):not([readonly]), ' +
  'select:not([disabled]):not(.skip-tab):not([readonly]), ' +
  'textarea:not([disabled]):not(.skip-tab):not([readonly]), ' +
  'button:not([disabled]):not(.skip-tab), ' +
  '[tabindex]:not([tabindex="-1"]):not(.skip-tab)'

// Connects to data-controller="document-form"
export default class extends Controller {
  static values = {
    isNew: Boolean, productsGoOut: Boolean, allowsForPurchase: Boolean, forClient: String,
    defaultPaymentTypeId: Number, creditPaymentTypeId: Number
  }
  static outlets = [ "document-item-form", "document-payment-form" ]
  static targets = [
    "documentDate", "documentTime", "stockStampingId",
    "stampingNumber", "branchNumber", "expeditionPointNumber",
    "documentNumber", "entityId", "entityDocumentNumber", "entityName",
    "entityDv", "itemsTableBody", "detailRow",
    "toFunctionaryId", "toFunctionaryName",
    "totalCell", "paymentField",
    "entitySearchModal", "entitySearchQuery",
    "functionarySearchPanel", "functionarySearchQuery",
    "productSearchModal", "productSearchQuery",
    "batchSearchModal"
  ]

  connect() {
    this.turnEnterInTab(this.element)
  }

  disconnect() {
    if (this.autoUpdateTimer) clearInterval(this.autoUpdateTimer)
    clearTimeout(this.entitySearchTimeout)
    clearTimeout(this.functionarySearchTimeout)
    clearTimeout(this.productSearchTimeout)
  }

  stockStampingIdSelected(event) {
    const selectedOption = event.target.selectedOptions[0]

    if (selectedOption && selectedOption.value !== "") {
      const { branchNumber, expeditionPointNumber, nextNumber } = selectedOption.dataset
      if (this.hasBranchNumberTarget) this.branchNumberTarget.innerText = branchNumber || ""
      if (this.hasExpeditionPointNumberTarget) this.expeditionPointNumberTarget.innerText = expeditionPointNumber || ""
      this.documentNumberTarget.value = nextNumber || ""
    } else {
      if (this.hasBranchNumberTarget) this.branchNumberTarget.innerText = ""
      if (this.hasExpeditionPointNumberTarget) this.expeditionPointNumberTarget.innerText = ""
      this.documentNumberTarget.value = ""
    }
  }

  documentDateTargetConnected() {
    if (this.isNewValue) this.autoUpdateDateTime()
  }

  autoUpdateDateTime() {
    if (this.autoUpdateTimer) return
    const tick = () => {
      const now = new Date()
      const today = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      this.documentTimeTarget.value = today.toISOString().slice(11, 19)
      this.documentDateTarget.value = today.toISOString().slice(0, 10)
    }
    tick()
    this.autoUpdateTimer = setInterval(tick, 1000)
  }

  async findBy(event) {
    const target = event.target
    const value = target.value
    if (!value) {
      this.setEntity()
      return
    }

    const type = target.dataset.type || "document_number"
    try {
      const response = await fetch(`/entities/find_by/${type}/${encodeURIComponent(value)}`, {
        headers: { Accept: "application/json" }
      })
      if (!response.ok) throw new Error("not found")
      const entity = await response.json()
      this.setEntity(entity)
    } catch {
      warning("Entidad no encontrada")
      this.setEntity()
      target.focus()
    }
  }

  setEntity(entity = {}) {
    this.entityIdTarget.value = entity.id || ""
    this.entityNameTarget.value = entity.name || ""
    this.entityDocumentNumberTarget.value = entity.document_number || ""
    if (this.hasEntityDvTarget) {
      this.entityDvTarget.innerText = entity.is_taxpayer ? `- ${entity.dv}` : ""
    }
  }

  entityModalOpened() {
    this.entitySearchQueryTarget.value = ""
    this.entitySearchQueryTarget.focus()
    this.fetchEntitySearch("")
  }

  entitySearchInputChanged(event) {
    clearTimeout(this.entitySearchTimeout)
    const query = event.target.value
    this.entitySearchTimeout = setTimeout(() => this.fetchEntitySearch(query), 300)
  }

  async fetchEntitySearch(query) {
    const response = await fetch(`/entities/search.turbo_stream?search=${encodeURIComponent(query)}`, {
      headers: { Accept: "text/vnd.turbo-stream.html" }
    })
    Turbo.renderStreamMessage(await response.text())
  }

  setEntityFromSearch(event) {
    this.entityIdTarget.value = event.params.entityId
    this.entityIdTarget.dispatchEvent(new Event("change", { bubbles: true }))
    window.HSOverlay.close(this.entitySearchModalTarget)
  }

  searchProduct(itemFormController) {
    this.activeProductRow = itemFormController
    this.productSearchQueryTarget.value = ""
    this.fetchProductSearch("")
    window.HSOverlay.open(this.productSearchModalTarget)
  }

  productModalOpened() {
    this.productSearchQueryTarget.focus()
  }

  productSearchInputChanged(event) {
    clearTimeout(this.productSearchTimeout)
    const query = event.target.value
    this.productSearchTimeout = setTimeout(() => this.fetchProductSearch(query), 300)
  }

  async fetchProductSearch(query) {
    const allowsForPurchase = this.allowsForPurchaseValue ? "&allows_for_purchase=true" : ""
    const response = await fetch(`/products/search.turbo_stream?search=${encodeURIComponent(query)}${allowsForPurchase}`, {
      headers: { Accept: "text/vnd.turbo-stream.html" }
    })
    Turbo.renderStreamMessage(await response.text())
  }

  setProductFromSearch(event) {
    if (!this.activeProductRow) return
    this.activeProductRow.productCodeTarget.value = event.params.productCode
    this.activeProductRow.productCodeTarget.dispatchEvent(new Event("change", { bubbles: true }))
    window.HSOverlay.close(this.productSearchModalTarget)
  }

  searchBatch(itemFormController, productId) {
    this.activeProductRow = itemFormController
    this.fetchBatchSearch(productId)
    window.HSOverlay.open(this.batchSearchModalTarget)
  }

  async fetchBatchSearch(productId) {
    const response = await fetch(`/products/${productId}/batches.turbo_stream`, {
      headers: { Accept: "text/vnd.turbo-stream.html" }
    })
    Turbo.renderStreamMessage(await response.text())
  }

  setBatchFromSearch(event) {
    if (!this.activeProductRow) return
    this.activeProductRow.setProductBatch(event.params.batchId, event.params.dueDate)
    window.HSOverlay.close(this.batchSearchModalTarget)
  }

  async findFunctionaryBy(event) {
    const target = event.target
    const functionaryId = target.value
    if (!functionaryId) {
      this.setFunctionary()
      return
    }

    try {
      const response = await fetch(`/functionaries/${functionaryId}.json`, { headers: { Accept: "application/json" } })
      if (!response.ok) throw new Error("not found")
      const functionary = await response.json()
      this.setFunctionary(functionary)
    } catch {
      warning("Funcionario no encontrado")
      this.setFunctionary()
      target.focus()
    }
  }

  setFunctionary(functionary = {}) {
    this.toFunctionaryIdTarget.value = functionary.id || ""
    this.toFunctionaryNameTarget.value = functionary.name || ""
  }

  searchFunctionary() {
    this.functionarySearchPanelTarget.classList.remove("hidden")
    this.functionarySearchQueryTarget.value = ""
    this.functionarySearchQueryTarget.focus()
    this.fetchFunctionarySearch("")
  }

  closeFunctionarySearch() {
    this.functionarySearchPanelTarget.classList.add("hidden")
  }

  functionarySearchInputChanged(event) {
    clearTimeout(this.functionarySearchTimeout)
    const query = event.target.value
    this.functionarySearchTimeout = setTimeout(() => this.fetchFunctionarySearch(query), 300)
  }

  async fetchFunctionarySearch(query) {
    const response = await fetch(`/functionaries/search.turbo_stream?search=${encodeURIComponent(query)}`, {
      headers: { Accept: "text/vnd.turbo-stream.html" }
    })
    Turbo.renderStreamMessage(await response.text())
  }

  setFunctionaryFromSearch(event) {
    this.toFunctionaryIdTarget.value = event.params.functionaryId
    this.toFunctionaryIdTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.closeFunctionarySearch()
  }

  paymentModeChanged({ target }) {
    const paymentTypeId = target.value === "credito" ? this.creditPaymentTypeIdValue : this.defaultPaymentTypeIdValue
    const firstOutlet = this.documentPaymentFormOutlets[0]
    if (firstOutlet) firstOutlet.setPaymentType(paymentTypeId)
  }

  detailRowTargetConnected(element) {
    this.turnEnterInTab(element)
  }

  turnEnterInTab(domElement) {
    const elements = domElement.querySelectorAll(FOCUSABLE_QUERY)
    elements.forEach((el) => {
      el.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault()
          const focusable = this.element.querySelectorAll(FOCUSABLE_QUERY)
          const idx = Array.prototype.indexOf.call(focusable, e.target)
          const next = focusable[idx + 1]
          if (next) {
            next.focus()
            if (typeof next.select === "function") next.select()
          }
        } else if (e.key === "Escape") {
          const backButton = document.querySelector(".back-button")
          if (backButton) {
            e.preventDefault()
            backButton.click()
          }
        }
      })
    })
  }

  calculateTotal() {
    let total = 0
    this.documentItemFormOutlets.forEach((outlet) => { total += outlet.totalValue || 0 })
    if (this.hasTotalCellTarget) this.totalCellTarget.innerText = `${numberWithCommas(Math.round(total))}Gs`

    if (this.paymentFieldTargets.length > 0) {
      let totalPayments = 0
      this.paymentFieldTargets.forEach((el) => {
        const row = el.closest("tr")
        const destroyField = row && row.querySelector("input[type=checkbox][name*='_destroy']")
        if (destroyField && destroyField.checked) return
        const numberValue = Number(el.value)
        if (!Number.isNaN(numberValue)) totalPayments += numberValue
      })
      if (totalPayments < total) {
        const firstPaymentField = this.paymentFieldTargets[0]
        const firstPaymentValue = Number(firstPaymentField.value) || 0
        firstPaymentField.value = total - (totalPayments - firstPaymentValue)
        firstPaymentField.dispatchEvent(new Event("keyup", { bubbles: true }))
      }
    }
  }
}
